import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { KpkApiUser } from "@keepeek/api-client";
import {
  currentUserQuery,
  DataViewKey,
  directExportNumberLimitSelector,
  DOWNLOAD_MANAGER_FORMAT_BY,
  downloadManagerDataviewKey,
  downloadManagerOpenState,
  DownloadManagerOpenStatus,
  downloadManagerOpenStatusState,
  downloadManagerPreferenceFormatByState,
  downloadManagerSelectedElementsState,
  elementsSelectionIdState,
  fetcherModeAtom,
  FormatType,
  KpkMedia,
  maxElementsByDownloadSelector,
  removeUndefinedDeep,
  selectedElements,
  SentryBreadcrumbCategory,
} from "@keepeek/commons";
import { DownloadManagerFormatsByType } from "@keepeek/refront-components";
import * as Sentry from "@sentry/nextjs";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { DownloadManagerFormatsByElement } from "../../../containers/DownloadManager/TypeByElement";
import { DownloadManager } from "../../../models/configuration/components/downloadManager";
import { Labels, TranslationSchema } from "../../../models/configuration/labels";
import { useConfiguration } from "../../config/hooks";
import { downloadManagerFetcherSelector } from "../../fetcher/selectors/downloadManager";
import { askDownloadStepState } from "../atoms/askDownloads";
import { downloadUrlForEmailState, DownloadUrlForEmailStatus } from "../atoms/emailSend";
import {
  downloadManagerSelectedFormatsByElementState,
  downloadManagerSelectedFormatsByTypeState,
} from "../atoms/formats";
import { requestDownloadIsEnableSelector } from "../selectors/askDownloads";
import {
  downloadManagerMergeSelectedFormatWithElementSelector,
  downloadManagerMergeSelectedFormatWithTypeSelector,
} from "../selectors/formats";
import {
  downloadFormats,
  extractAskDownloadElementIds,
  findDownloadManagerLabelByKeyAndLocale,
  formatTypeIdAskDownload,
} from "../utils";
import useAskDownloads from "./askDownloads";
import { UseDownloadConditions, useDownloadConditions } from "./conditions";
import { UseFormatFavorites, useFormatFavorites } from "./formatFavorites";

type UseDownloadManagerType = {
  open: boolean;
  openDownloadManager: (dataviewKey: DataViewKey) => void;
  closeDownloadManager: () => void;
  dataviewKey: DataViewKey | undefined;
  selection: number[];
  elementsToDownloadCount: number;
  goToStepOne: boolean;
  elementsDownloadRequestCount: number;
  emptyFormatsByTypes: boolean;
  emptyFormatsByElements: boolean;
  formatsByTypes: DownloadManagerFormatsByType[];
  formatsByElements: DownloadManagerFormatsByElement[];
  loadingByTypes: boolean;
  loadingByElements: boolean;
  selectFormatByType: (id: FormatType["id"]) => void;
  selectFormatByElement: (element: KpkMedia["id"], format: FormatType["id"]) => void;
  download: (sendByEmail: boolean) => void;
  clickDownloadLoading: boolean;
  askDownloadStep: boolean;
  askDownloadElementIds: KpkMedia["id"][];
  conditions: UseDownloadConditions;
  formatFavorites: UseFormatFavorites;
  downloadUrlForEmail: { url: string; status: DownloadUrlForEmailStatus };
  setDownloadUrlForEmail: ({
    url,
    status,
  }: {
    url: string;
    status: DownloadUrlForEmailStatus;
  }) => void;
};

export const useDownloadManager = (): UseDownloadManagerType => {
  // Setup
  const selection = useRecoilValue(downloadManagerSelectedElementsState);
  const [open, setOpen] = useRecoilState(downloadManagerOpenState);
  const [dataviewKey, setDataviewKey] = useRecoilState(downloadManagerDataviewKey);
  const snackBar = useSnackbar();
  const labelsConfigSection = useConfiguration<Labels[]>(CONFIGURATION_SECTION_PATH.LABELS);
  const { locale } = useRouter();
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      Sentry.addBreadcrumb({
        category: SentryBreadcrumbCategory.VISUAL_FEEDBACK,
        message: "downloadManagerOpenState: open",
      });
    }
  }, [open]);

  // By Types
  const queryByTypes = useRecoilValueLoadable(downloadManagerMergeSelectedFormatWithTypeSelector);
  const setSelectedFormatByType = useSetRecoilState(downloadManagerSelectedFormatsByTypeState);

  const setDownloadManagerSelectedFormatsByTypeState = useSetRecoilState(
    downloadManagerSelectedFormatsByTypeState,
  );

  const loadingByTypes: boolean = queryByTypes.state === "loading";

  const formatsByTypes: DownloadManagerFormatsByType[] = useMemo(
    () => (queryByTypes.state === "hasValue" ? queryByTypes.contents.formatsByTypes : []),
    [queryByTypes.state, queryByTypes.contents],
  );
  const selectionId: string | null =
    queryByTypes.state === "hasValue" ? queryByTypes.contents.selectionId : null;

  // Import other usefull hooks
  const {
    lastUsed,
    setLastUsed,
    favorites,
    setFavorites,
    selectFavorite,
    selectFavoritesByDownload,
  } = useFormatFavorites();
  const { state, onChange, error, hasError, loading, csu, cgu } =
    useDownloadConditions(selectionId);
  const { keepSelectedElementsAfterDownload } = useConfiguration<DownloadManager>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_DOWNLOAD_MANAGER,
  ) ?? { keepSelectedElementsAfterDownload: undefined };

  const elementsToDownloadCount = useMemo(() => {
    let count = 0;
    formatsByTypes.forEach((fbt) => {
      const haveFormats = fbt.formats.some(
        (f) => f.id !== `${fbt.type}_${formatTypeIdAskDownload}` && f.active,
      );
      if (haveFormats) {
        count += fbt.elements.length;
      }
    });
    return count;
  }, [formatsByTypes]);

  const goToStepOne: boolean = useMemo(
    () => elementsToDownloadCount > 0 || formatsByTypes.some((ft) => ft.formats.length > 1),
    [elementsToDownloadCount, formatsByTypes],
  );

  const elementsDownloadRequestCount = useMemo(() => {
    let count = 0;
    formatsByTypes.forEach((fbt) => {
      const haveAskDownload = fbt.formats.some(
        (f) => f.id === `${fbt.type}_${formatTypeIdAskDownload}` && f.active,
      );
      if (haveAskDownload) {
        count += fbt.elements.length;
      }
    });
    return count;
  }, [formatsByTypes]);

  const selectFormatByType = useMemo(
    () => (id: FormatType["id"]) => {
      setSelectedFormatByType((currVal) => {
        // First, if the value is already in, uncheck it
        if (currVal.includes(id)) {
          return currVal.filter((value) => value !== id);
        }
        // TODO: Remove this whole check when the API can take more than one format per type
        // Then check this,
        // For now, only one format per type is authorized for the API
        // Uncheck every other from the same type to only keep one by type
        const siblings: FormatType["id"][] = [];
        formatsByTypes.forEach((formatsByType: DownloadManagerFormatsByType) => {
          let isType = false;
          formatsByType.formats.forEach((format) => {
            if (format.id === id) {
              isType = true;
            }
          });
          if (isType) {
            formatsByType.formats.forEach((format) => {
              siblings.push(format.id);
            });
          }
        });
        if (siblings.length > 0) {
          return uniq([id, ...currVal.filter((value) => !siblings.includes(value))]);
        }

        // If not, just add it
        return uniq([id, ...currVal]);
      });
    },
    [formatsByTypes, setSelectedFormatByType],
  );

  const openDownloadManager = (dataViewKey: DataViewKey) => {
    setDataviewKey(dataViewKey);
    setOpen(true);
  };

  const closeDownloadManager = () => {
    setOpen(false);
  };

  const setAttachmentsElementsId = useSetRecoilState(elementsSelectionIdState);

  useEffect(() => {
    setAttachmentsElementsId(selection);
  }, [selection, setAttachmentsElementsId]);

  useEffect(() => {
    // if no format is selected on init then select the first format from the list
    // It will also always keep at least the first format selected in case the user unselect all
    formatsByTypes.forEach((formatsByType) => {
      let hasActive = false;
      formatsByType.formats.forEach((format) => {
        if (format.active) {
          hasActive = true;
        }
      });
      // Check if there's no active and if we have formats
      if (!hasActive && formatsByType.formats.length > 0) {
        // Select the first
        selectFormatByType(formatsByType.formats[0].id);
      }
    });
  }, [formatsByTypes, selectFormatByType]);

  const requestDownloadIsEnableQuery = useRecoilValueLoadable(requestDownloadIsEnableSelector);
  const requestDownloadIsEnable = requestDownloadIsEnableQuery.contents;

  useEffect(() => {
    if (requestDownloadIsEnable === false) {
      // Check if it's possible to download at least one format
      let countImpossibleFormatsDownloadByTypes = 0;
      formatsByTypes.forEach((formatsByType) => {
        if (formatsByType.formats.length === 0) {
          countImpossibleFormatsDownloadByTypes += 1;
        }
      });
      const impossibleToDownloadForEveryTypes =
        !loadingByTypes && open
          ? countImpossibleFormatsDownloadByTypes === formatsByTypes.length
          : false;
      // If there's no possible formats to download
      if (impossibleToDownloadForEveryTypes) {
        // Close the download Manager
        setOpen(false);
        Sentry.addBreadcrumb({
          category: SentryBreadcrumbCategory.VISUAL_FEEDBACK,
          message: "downloadManagerOpenState: close",
        });

        const snackBarLabelKey =
          selection.length === 1
            ? "download-manager.askDownload.no-element.label"
            : "download-manager.askDownload.no-elements.label";

        const snackBarLabel: TranslationSchema | undefined = findDownloadManagerLabelByKeyAndLocale(
          labelsConfigSection,
          snackBarLabelKey,
          locale,
        );

        // Snackbar will be removed from project one day
        // A bug display two snackbar on Guerlain ReFront : https://jira.keepeek.com/browse/RF-1969
        // Quick win to be able to hide the wanted one
        if (snackBarLabel && !snackBarLabel.empty) {
          // If there is an override and the override is not empty
          snackBar.enqueueSnackbar(snackBarLabel?.value, {
            variant: "info",
          });
        } else if (!snackBarLabel) {
          // If there is no override display original notification
          snackBar.enqueueSnackbar(t(snackBarLabelKey), {
            variant: "info",
          });
        }
      }
    }
  }, [
    labelsConfigSection,
    formatsByTypes,
    loadingByTypes,
    locale,
    open,
    selection.length,
    setOpen,
    snackBar,
    t,
    requestDownloadIsEnable,
  ]);

  // By Elements
  const queryByElements = useRecoilValueLoadable(
    downloadManagerMergeSelectedFormatWithElementSelector,
  );
  const setSelectedFormatByElement = useSetRecoilState(
    downloadManagerSelectedFormatsByElementState,
  );

  const loadingByElements: boolean = queryByElements.state === "loading";

  const formatsByElements: DownloadManagerFormatsByElement[] = useMemo(
    () => (queryByElements.state === "hasValue" ? queryByElements.contents : []),
    [queryByElements.contents, queryByElements.state],
  );

  const selectFormatByElement = (element: KpkMedia["id"], format: FormatType["id"]) => {
    setSelectedFormatByElement((currVal) => {
      const indexSelectedElement = currVal.findIndex((s) => s.element === element);
      if (indexSelectedElement !== -1) {
        const removedSelection = currVal.filter((value) => value.element !== element);
        if (format) {
          return removedSelection;
        }
        return uniqBy([{ element, format }, ...removedSelection], "element");
      }
      return uniqBy([{ element, format }, ...currVal], "element");
    });
  };

  // Download
  // TODO: update the selection from any source
  const setSetSelectedElements = useSetRecoilState(selectedElements(DataViewKey.SEARCH));
  const userQuery = useRecoilValueLoadable(currentUserQuery);
  const user: KpkApiUser = useMemo(
    () => (userQuery.state === "hasValue" ? userQuery.contents : {}),
    [userQuery.contents, userQuery.state],
  );

  const {
    askDownloadElementIds,
    clickAskDownload,
    askDownloadStep,
    setAskDownloadElementIds,
    isFieldsInError: isAskDownloadsFieldsInError,
  } = useAskDownloads(open, closeDownloadManager);

  const mode = useRecoilValue(fetcherModeAtom);

  const downloadManagerFetcherSelectorQuery = useRecoilValueLoadable(
    downloadManagerFetcherSelector(mode),
  );

  const handleDownloadFormats = useCallback(
    ({
      shouldCreateAskDownload,
      close,
      sendByEmail,
      setDownloadUrlForEmail,
    }: {
      shouldCreateAskDownload: boolean;
      close: boolean;
      sendByEmail: boolean;
      setDownloadUrlForEmail: ({
        url,
        status,
      }: {
        url: string;
        status: DownloadUrlForEmailStatus;
      }) => void;
    }) => {
      if (downloadManagerFetcherSelectorQuery.state === "hasValue") {
        const { deleteSelection, postDownloadRequests, getDownloadRequests } =
          downloadManagerFetcherSelectorQuery.contents;
        // Block download if we do not accept the CGU
        if ((cgu?.enable || csu?.enable) && !state) {
          hasError();
        } else if (!isAskDownloadsFieldsInError()) {
          // save last used format
          const lastUsedFormats: FormatType["id"][] = removeUndefinedDeep(
            formatsByTypes.flatMap((fbt) => fbt.formats.map((f) => (f.active ? f.id : undefined))),
          );
          // Warning: setting favorites will trigger a Recoil update of formatsByTypes
          selectFavoritesByDownload(lastUsedFormats);
          // Create the standalone download
          downloadFormats(
            deleteSelection,
            postDownloadRequests,
            getDownloadRequests,
            formatsByTypes,
            snackBar,
            user,
            t,
            selectionId,
            sendByEmail,
            setDownloadUrlForEmail,
          );
          if (shouldCreateAskDownload) {
            // Create download request
            clickAskDownload();
          }
          if (close) {
            // close the modal
            setOpen(false);
            Sentry.addBreadcrumb({
              category: SentryBreadcrumbCategory.VISUAL_FEEDBACK,
              message: "downloadManagerOpenState: close",
            });
          }
        }
      }
    },
    [
      clickAskDownload,
      cgu?.enable,
      csu?.enable,
      state,
      hasError,
      downloadManagerFetcherSelectorQuery.contents,
      downloadManagerFetcherSelectorQuery.state,
      selectFavoritesByDownload,
      formatsByTypes,
      isAskDownloadsFieldsInError,
      selectionId,
      setOpen,
      snackBar,
      t,
      user,
    ],
  );

  // Save the click for positive UX when the user click and it's still loading formats
  const [clickDownload, setClickDownload] = useState<boolean>(false);
  const [downloadUrlForEmail, setDownloadUrlForEmail] = useRecoilState(
    downloadUrlForEmailState(selectionId),
  );

  // Use a ref to avoid download effect loop
  const isDownloading = useRef<boolean>();
  const sendByEmailRef = useRef<boolean>(false);

  const download = (sendByEmail: boolean) => {
    // Reset download flag
    isDownloading.current = false;
    sendByEmailRef.current = sendByEmail;
    // Force render loop
    setClickDownload(true);
  };

  useEffect(() => {
    // Download only when we have finished loading and use a ref to avoid looping on download
    // due to formatsByTypes and handleDownloadFormats update (favorites are updated in handleDownloadFormats)
    if (!isDownloading.current && clickDownload && !loadingByTypes) {
      // Set downloading flag
      isDownloading.current = true;
      // Remove the saved click
      setClickDownload(false);
      Sentry.addBreadcrumb({
        category: SentryBreadcrumbCategory.USER_INTERACTION,
        message: "downloadManager: download",
      });
      if (elementsToDownloadCount > 0 && elementsDownloadRequestCount === 0) {
        handleDownloadFormats({
          shouldCreateAskDownload: false,
          close: !sendByEmailRef.current,
          sendByEmail: sendByEmailRef.current,
          setDownloadUrlForEmail,
        });
        // clear data view selection
        if (!keepSelectedElementsAfterDownload) {
          setSetSelectedElements([]);
          setDownloadManagerSelectedFormatsByTypeState([]);
        }
      } else if (askDownloadElementIds.length > 0 && askDownloadStep) {
        handleDownloadFormats({
          shouldCreateAskDownload: true,
          close: !sendByEmailRef.current,
          sendByEmail: sendByEmailRef.current,
          setDownloadUrlForEmail,
        });
        setDownloadManagerSelectedFormatsByTypeState([]);
      } else {
        // Go to step 2
        setAskDownloadElementIds(extractAskDownloadElementIds(formatsByTypes));
      }
    }
  }, [
    askDownloadElementIds.length,
    askDownloadStep,
    clickDownload,
    elementsDownloadRequestCount,
    elementsToDownloadCount,
    formatsByTypes,
    handleDownloadFormats,
    keepSelectedElementsAfterDownload,
    loadingByTypes,
    setAskDownloadElementIds,
    setDownloadManagerSelectedFormatsByTypeState,
    setDownloadUrlForEmail,
    setOpen,
    setSetSelectedElements,
  ]);

  const preferenceFormatBy = useRecoilValue(downloadManagerPreferenceFormatByState);
  const setAskDownloadStep = useSetRecoilState(askDownloadStepState);
  useEffect(() => {
    if (
      elementsToDownloadCount === 0 &&
      !loadingByTypes &&
      preferenceFormatBy === DOWNLOAD_MANAGER_FORMAT_BY.TYPE
    ) {
      // Go directly to step 2 when no elements to download
      if (formatsByTypes.every((ft) => ft.formats.length === 1)) {
        setAskDownloadElementIds(extractAskDownloadElementIds(formatsByTypes));
      }
    }
    if (!open) {
      setAskDownloadStep(false);
    }
  }, [
    elementsToDownloadCount,
    formatsByTypes,
    loadingByTypes,
    preferenceFormatBy,
    setAskDownloadElementIds,
    open,
    setAskDownloadStep,
  ]);

  // Manage error
  const [status, setStatus] = useRecoilState(downloadManagerOpenStatusState);
  const maxElementsQuery = useRecoilValueLoadable(maxElementsByDownloadSelector);
  const maxElements = maxElementsQuery.state === "hasValue" ? maxElementsQuery.contents : 0;
  const directExportNumberLimitLoadable = useRecoilValueLoadable(directExportNumberLimitSelector);
  const directExportNumberLimit =
    directExportNumberLimitLoadable.state === "hasValue"
      ? directExportNumberLimitLoadable.contents
      : undefined;
  useEffect(() => {
    switch (status) {
      case DownloadManagerOpenStatus.MAX_REACHED:
        snackBar.enqueueSnackbar(
          t("download-manager.status.max-reached.label", { count: maxElements }),
          { variant: "warning" },
        );
        break;
      case DownloadManagerOpenStatus.MAX_REACHED_ANONYMOUS:
        snackBar.enqueueSnackbar(
          t("download-manager.status.max-reached-anonymous.label", {
            count: directExportNumberLimit,
          }),
          { variant: "warning" },
        );
        break;
      default:
        break;
    }

    return () => {
      // Reset after print
      setStatus(DownloadManagerOpenStatus.ALLOWED);
    };
  }, [directExportNumberLimit, maxElements, setStatus, snackBar, status, t]);

  const emptyFormatsByTypes = useMemo(
    () => !formatsByTypes.every((type) => type.formats.length !== 0),
    [formatsByTypes],
  );

  const emptyFormatsByElements = useMemo(
    () => !formatsByElements.every((element) => element.formats.length !== 0),
    [formatsByElements],
  );

  return {
    open,
    openDownloadManager,
    closeDownloadManager,
    dataviewKey,
    selection,
    elementsToDownloadCount,
    goToStepOne,
    elementsDownloadRequestCount,
    emptyFormatsByTypes,
    emptyFormatsByElements,
    formatsByTypes,
    formatsByElements,
    loadingByTypes,
    loadingByElements,
    selectFormatByType,
    selectFormatByElement,
    download,
    clickDownloadLoading: clickDownload,
    askDownloadStep,
    askDownloadElementIds,
    conditions: { state, onChange, error, hasError, loading, csu, cgu },
    formatFavorites: {
      lastUsed,
      setLastUsed,
      favorites,
      setFavorites,
      selectFavorite,
      selectFavoritesByDownload,
    },
    downloadUrlForEmail,
    setDownloadUrlForEmail,
  };
};
